import PrivacyModal from "@app-components/privacy-modal/privacy-modal.vue";
import usei18n from "@app-i18n/index";
import { PolicyTermType } from "@app-rest/common.interfaces";
import { redirectToMaintenanceMode } from "@app-router/helpers";
import router from "@app-router/index";
import { policyPage } from "@app-router/routes/policyTerms";
import useUserStore, { IPrivacyChoice, logout } from "@app-store/user";
import { QButton, QModalProps, useModalsUtilities } from "@qamf/lighthouse";
import { ComputedRef, defineComponent, h, Ref, ref, VNode } from "vue";

import { toastSuccess } from "./toasts";

interface IModalOption {
	label: string
	value: any
	variant?: string
}

function getDefaultOptions(): QModalProps {
	const { translateKey } = usei18n();
	return {
		bodyClass: "",
		cancelTitle: translateKey("label_cancel"),
		cancelVariant: "outline-primary",
		centered: true,
		footerClass: "",
		dialogClass: "",
		modalClass: "modal-utility",
		noCloseOnBackdrop: true,
		noCloseOnEsc: true,
		okTitle: translateKey("label_ok"),
		okVariant: "primary",
		scrollable: true,
		modelValue: true
	};
}
export function modalEmpty(message: string | VNode[], overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	const id = "modal-empty" + Date.now();
	localOptions.id = localOptions.id ?? id;
	localOptions.modalClass = localOptions.modalClass + " modal-empty";
	localOptions.footerClass = localOptions.footerClass || "d-none";
	localOptions.hideHeaderClose = false;

	const messageNodes = typeof message === "string"
		? [h("div", message)]
		: message;
	const messageWrapper = h("div", { class: "message-wrapper" }, messageNodes);
	return launchModal([messageWrapper], localOptions);
}

interface IModalAgreeOptions extends QModalProps {
	agreementContent?: VNode[];
	agreementFooterContent?: VNode[];
	agreementDisabled?: Ref<boolean> | ComputedRef<boolean>;
}

export function modalAgree(message: VNode[], overrides?: IModalAgreeOptions) {
	const { launchModal, closeModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const { agreementContent = [], agreementFooterContent = [], agreementDisabled, ...modalOverrides } = overrides ?? {};
	const localOptions = Object.assign({}, defOpts, modalOverrides);
	const id = "modal-agree-" + Date.now();
	localOptions.id = localOptions.id ?? id;
	localOptions.modalClass = localOptions.modalClass + " modal-agree";
	localOptions.hideHeaderClose = true;
	localOptions.hideHeader = true;
	localOptions.hideFooter = true;
	localOptions.noCloseOnBackdrop = true;
	localOptions.noCloseOnEsc = true;
	const AgreementButton = defineComponent({
		setup() {
			return () => h(QButton, {
				size: "lg",
				class: "w-100 my-5",
				variant: localOptions.okVariant,
				disabled: agreementDisabled?.value,
				onClick: () => closeModal(true)
			}, () => localOptions.okTitle);
		}
	});
	const footerContent = [...agreementFooterContent];

	return launchModal([
		...message,
		...agreementContent,
		h(AgreementButton),
		...footerContent,
	], localOptions);
}

export function modalError(message: string | VNode[], overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-error";
	localOptions.footerClass = "d-none";
	localOptions.hideHeaderClose = false;
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Error";
	return launchModal(message, localOptions);
}
export function modalWarning(message: string | VNode[], overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-warning";
	localOptions.footerClass = "d-none";
	localOptions.hideHeaderClose = false;
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Warning";
	return launchModal(message, localOptions);
}
export async function modalFatal(overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const { translateKey } = usei18n();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-error";
	localOptions.footerClass = "d-none";
	localOptions.hideHeaderClose = false;
	localOptions.title = translateKey("common_error_modal_something_went_wrong");
	localOptions.centered = true;
	localOptions.noCloseOnEsc = true;
	localOptions.noCloseOnBackdrop = true;
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Fatal";
	const message = [
		h("div",
			[
				h("p", translateKey("common_error_modal_page_does_not_work")),
				h("p", translateKey("common_error_modal_message_please_close_this_window_to_refresh"))
			]
		)
	];
	return launchModal(message, localOptions);
}
export async function modalCartError(overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const { translateKey } = usei18n();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-error";
	localOptions.hideHeaderClose = true;
	localOptions.title = translateKey("common_error_modal_something_went_wrong");
	localOptions.centered = true;
	localOptions.noCloseOnEsc = true;
	localOptions.noCloseOnBackdrop = true;
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Cart-Error";
	const message = [
		h("div",
			[
				h("p", translateKey("common_error_modal_page_does_not_work")),
				h("p", translateKey("common_error_modal_message_please_close_this_window_to_start_a_new_reservation"))
			]
		)
	];
	return launchModal(message, localOptions);
}
export function modalConfirm(message: string | VNode[], overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-confirm";
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Confirm";
	return launchModal(message, localOptions);
}
export function modalWarningConfirm(message: string | VNode[], overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-warning modal-confirm";
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Warning-Confirm";
	return launchModal(message, localOptions);
}
export function modalWarningOneButton(message: string | VNode[], overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-warning modal-confirm modal-one-button";
	localOptions.footerClass = localOptions.modalClass + " border-top-0";
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Warning-One-Button";
	return launchModal(message, localOptions);
}
export function modalOk(message: string | VNode[], overrides?: QModalProps) {
	const { launchModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	localOptions.modalClass = localOptions.modalClass + " modal-ok";
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Ok";
	return launchModal(message, localOptions);
}
export function modalOptions(message: string | VNode[], options: IModalOption[], overrides?: QModalProps): Promise<boolean> {
	const { launchModal, closeModal } = useModalsUtilities();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts, (overrides ?? {}));
	const id = "modal-with-options-" + Date.now();
	localOptions.id = id;
	localOptions.hideHeaderClose = true;
	localOptions.modalClass = localOptions.modalClass + " modal-utility modal-with-options modal-confirm";
	localOptions.footerClass = "d-none";
	localOptions["data-test"] = localOptions["data-test"] || "Modal-Options";

	return new Promise(resolve => {
		const messageNodes = typeof message === "string"
			? [h("div", message)]
			: message;
		const messageWrapper = h("div", { class: "message-wrapper" }, messageNodes);
		const optionsWrapper = h(
			"template",
			{ class: "options-wrapper modal-footer", attrs: { options: options.length }, slot: "footer" },
			options.map(option => {
				const btn = h(QButton, {
					variant: option.variant ?? "outline-primary" as any,
					onClick: () => {
						resolve(option.value);
						closeModal();
					}
				}, () => [option.label]);
				return btn;
			})
		);
		return launchModal([messageWrapper, optionsWrapper], localOptions) as any;
	});
}
export function modalPrivacy(companyId: number, centerName: string) {
	const { launchModal, closeModal } = useModalsUtilities();
	const { translateKey } = usei18n();
	const defOpts = getDefaultOptions();
	const localOptions = Object.assign({}, defOpts);
	localOptions.modalClass = localOptions.modalClass + " modal-privacy";
	localOptions.footerClass = "d-none";
	localOptions.hideHeaderClose = false;
	localOptions.title = translateKey("signup_modal_title");
	localOptions["data-test"] = "Modal-Privacy";

	const appRouter = ref(router);
	const policyRoute = router.resolve({
		name: policyPage,
		params: {
			companyId: appRouter.value.currentRoute.params.companyId,
			systemId: appRouter.value.currentRoute.params.systemId,
			type: PolicyTermType.PrivacyPolicy
		},
		query: appRouter.value.currentRoute.query
	});
	const urlPrivacyCompany = policyRoute.href;

	const message = h(PrivacyModal, {
		privacyLink: urlPrivacyCompany,
		onProceed: async (data: IPrivacyChoice[]) => {
			await useUserStore().completeUserSignup({
				companyId,
				privacyData: {
					privacyChoices: data
				}
			});
			toastSuccess(translateKey("signup_complete", { centerName }));
			closeModal();
		},
		onDecline: async () => {
			await logout();
			closeModal();
		}
	});
	return launchModal([message], localOptions);
}

export const modalMaintenanceMode = async () => {
	const { translateKey } = usei18n();
	await modalWarningOneButton(translateKey("cart_sorry_our_online_booking_service_is_unavailable"), {
		title: translateKey("cart_reservation_request_failed"),
		okTitle: translateKey("label_close"),
		"data-test": "Modal-MaintenanceMode"
	});
	return redirectToMaintenanceMode();
};
